/**
 * The move (RFC 0339 3e): a project's comments to the comments documents of
 * the pages and sessions they are about, and its pairings to their pages.
 *
 * Run by the daemon for each project it hosts, every round, because it costs
 * one read once there is nothing left. KEYED BY ID: two devices running it at
 * once write the same key with the same value, and running it twice changes
 * nothing. A project's entry is deleted only once its copy is on a ready
 * handle, so a page or session not synced here keeps its entries on the
 * project for the next pass, and readers read both meanwhile.
 */

import { isValidDocumentId, type Repo } from '@automerge/automerge-repo/slim'
import {
  type CommentSubject,
  commentsDocFor,
  indexEntryOf,
  indexProjectFor,
  mapWithConflicts,
  readyHandle,
  subjectOf,
  writeCommentIndex,
} from '../model/comments/store'
import type { CommentEntry, CommentIndexEntry } from '../model/comments/types'
import type { DocumentDoc } from '../model/documents/types'
import { changeProject } from '../model/project/scalars'
import type { Pairing, ProjectDoc } from '../model/project/types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

export interface MoveResult {
  /** Comments now in a comments document and gone from the project. */
  comments: number
  /** Pairings now on their page and gone from the project. */
  pairings: number
  /** Left for the next pass: their page or session is not here yet. */
  left: number
  /** Comments naming neither a page nor a session (5): kept, and counted. */
  nowhere: number
}

/** A plain copy, so no Automerge proxy is assigned into another document. */
function plain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

export async function moveProjectComments(
  repo: Repo,
  projectId: string,
): Promise<MoveResult> {
  const result: MoveResult = { comments: 0, pairings: 0, left: 0, nowhere: 0 }
  const project = await readyHandle<ProjectDoc>(repo, projectId)
  const doc = project?.doc()
  if (!project || !doc) return result

  // Comments, grouped by where they go. Each is remembered as it was read, so
  // the project's entry is deleted only if nothing changed it meanwhile (a
  // resolve or a reply can land on it while the pages are being opened).
  const read = new Map<string, string>()
  const bySubject = new Map<
    string,
    { subject: CommentSubject; entries: CommentEntry[] }
  >()
  for (const raw of Object.values(doc.comments ?? {})) {
    if (!raw?.id) continue
    const entry = plain(raw)
    const subject = subjectOf(entry.target, projectId)
    if (!subject) {
      result.nowhere++
      continue
    }
    read.set(entry.id, JSON.stringify(entry))
    const key = `${subject.kind}:${subject.id}`
    const group = bySubject.get(key) ?? { subject, entries: [] }
    group.entries.push(entry)
    bySubject.set(key, group)
  }

  // Copied first. Until it is indexed, nothing but this pass writes the copy
  // (every write is routed to the project's entry), so it is overwritten with
  // what the project holds now: a pass that found the entry changed copies
  // the newer one the next time.
  const indexed = mapWithConflicts<CommentIndexEntry>(doc, 'commentIndex')
  const copied = new Map<
    string,
    { entry: CommentIndexEntry; indexOn: string }
  >()
  for (const { subject, entries } of bySubject.values()) {
    let handle: Awaited<ReturnType<typeof commentsDocFor>>
    try {
      handle = await commentsDocFor(repo, subject)
    } catch {
      result.left += entries.length
      continue
    }
    handle.change((d: Draft) => {
      if (!d.comments) d.comments = {}
      for (const e of entries) {
        if (!indexed[e.id] || !d.comments[e.id]) d.comments[e.id] = e
      }
    })
    const now = handle.doc()?.comments ?? {}
    const indexOn = await indexProjectFor(repo, subject, projectId)
    for (const e of entries) {
      const copy = now[e.id]
      if (!copy) {
        result.left++
        continue
      }
      copied.set(e.id, { entry: indexEntryOf(copy, subject), indexOn })
    }
  }

  // Pairings, each to its page, remembered the same way. A pairing to
  // something that is not a document (a code file) has no page and stays.
  const pairingsIndexed = mapWithConflicts(doc, 'pairingIndex')
  const pairingsRead = new Map<string, string>()
  const placed: { pairing: Pairing; onPage: boolean }[] = []
  for (const raw of Object.values(doc.pairings ?? {})) {
    if (!raw?.id || !raw.documentId) continue
    const pairing = plain(raw)
    const documentId = String(pairing.documentId)
    if (!isValidDocumentId(documentId)) continue
    const page = await readyHandle<DocumentDoc>(repo, documentId)
    if (!page) {
      result.left++
      continue
    }
    pairingsRead.set(pairing.id, JSON.stringify(pairing))
    page.change((d: Draft) => {
      if (!d.pairings) d.pairings = {}
      if (d.pairings[pairing.id]) {
        if (!pairingsIndexed[pairing.id]) d.pairings[pairing.id] = pairing
        return
      }
      // A pairing for the same session written on the page since the split
      // is newer and the person's latest word: it stays, and this one goes.
      const onPage = mapWithConflicts<Pairing>(page.doc(), 'pairings')
      const already = Object.values(onPage).some(
        (p) => p.sessionId === pairing.sessionId,
      )
      if (!already) d.pairings[pairing.id] = pairing
    })
    const held = Object.values(
      mapWithConflicts<Pairing>(page.doc(), 'pairings'),
    )
    const onPage = held.some((p) => p.id === pairing.id)
    if (!onPage && !held.some((p) => p.sessionId === pairing.sessionId)) {
      result.left++
      continue
    }
    placed.push({ pairing, onPage })
  }

  if (copied.size === 0 && placed.length === 0) return result

  // Deleted and indexed in ONE change to this project, and only where the
  // entry is still what was copied. Indexing is what routes later writes to
  // the copy, so an entry is never both unindexed and gone.
  const moved: string[] = []
  const movedPairings: string[] = []
  changeProject(project, (d: Draft) => {
    if (copied.size > 0 && !d.commentIndex) d.commentIndex = {}
    for (const [id, { entry }] of copied) {
      const current = d.comments?.[id]
      if (!current || JSON.stringify(plain(current)) !== read.get(id)) {
        result.left++
        continue
      }
      d.commentIndex[id] = entry
      delete d.comments[id]
      moved.push(id)
    }
    if (placed.length > 0 && !d.pairingIndex) d.pairingIndex = {}
    for (const { pairing: p, onPage } of placed) {
      const current = d.pairings?.[p.id]
      if (
        !current ||
        JSON.stringify(plain(current)) !== pairingsRead.get(p.id)
      ) {
        result.left++
        continue
      }
      // Superseded by a newer pairing on the page, which indexed itself.
      if (onPage) {
        d.pairingIndex[p.id] = {
          sessionId: String(p.sessionId),
          documentId: String(p.documentId),
          enabled: p.enabled !== false,
        }
      }
      delete d.pairings[p.id]
      movedPairings.push(p.id)
    }
  })

  // A page another project lists is indexed there too.
  const elsewhere = new Map<string, Record<string, CommentIndexEntry>>()
  for (const id of moved) {
    const c = copied.get(id)
    if (!c || c.indexOn === projectId) continue
    const entries = elsewhere.get(c.indexOn) ?? {}
    entries[id] = c.entry
    elsewhere.set(c.indexOn, entries)
  }
  for (const [indexOn, entries] of elsewhere) {
    await writeCommentIndex(repo, indexOn, entries)
  }

  result.comments = moved.length
  result.pairings = movedPairings.length
  return result
}
